/**
 * Settings API functions for Lobby2.0
 * Handles fetching and parsing lobby configuration from the backend
 * 
 * PERFORMANCE OPTIMIZED:
 * - Cached transformation results
 * - Memoized expensive operations
 * - Reduced redundant processing
 */

import { getSettingsUrl, normalize, SECTION_NAMES } from '@repo/common';
import type { 
  SettingsApiResponse, 
  ParsedSettingsResponse, 
  ApiGameSettings 
} from '../../../types/api/api.types';
import mockSettingsResponse from './mockSettingsResponse';
import type IGameSettings from '../../../types/models/IGameSettings';
import type ISectionSettings from '../../../types/models/ISectionSettings';
import { isGameNew } from '../../../shared/utils/dateHelpers';
import { buildGameBanners, BANNER_TAG } from '../../../features/games/constants/gameBanners';
import { MAX_TOP_GAMES } from '../../../constants';


// Performance optimizations
let cachedMockSettings: ParsedSettingsResponse | null = null;
const transformedResponseCache = new Map<string, SettingsApiResponse>();

// Helper to determine if we're in development mode
const isDevelopmentMode = (apiHost: string): boolean => {
  return !apiHost ||
         apiHost.includes('example.com') ||
         apiHost.includes('localhost') ||
         apiHost === '';
};

/**
 * A section may have an expiration date and/or an introduction date.
 * If there is an expiration date and it has passed, don't show the section.
 * If there is an introduction date and it hasn't passed, don't show the section.
 * If neither condition applies or they are both valid, show the section.
 */
const filterExpAndIntSections = (section: { expiration?: string; introduction?: string }): boolean => {
  const currentDate = new Date();

  if (section.expiration && currentDate >= new Date(section.expiration)) {
    return false; // The section has expired
  }

  if (section.introduction && currentDate <= new Date(section.introduction)) {
    return false; // The section is not yet introduced
  }

  return true; // The section is valid
};

// Memoized transformation functions to avoid repeated processing
const memoizedTransformations = (() => {
  const cache = new WeakMap<SettingsApiResponse, SettingsApiResponse>();
  return (data: SettingsApiResponse): SettingsApiResponse => {
    const cached = cache.get(data);
    if (cached) {
      return cached;
    }
    
    // Apply transformation functions
    const functionsToMutateResponse = [
      renameSectionsForBackwardCompatibility,
      buildNewGameSection,
      addEGTCurrency, // Consider to remove this function if the EGT currency included in the response
      mockInstantGamesDenominations,
      addSingleProgressivePanel,
      setVerticalBanners,
    ];

    let transformedData = data;
    functionsToMutateResponse.forEach((func) => {
      try {
        // A single malformed transform must not fail the whole load.
        transformedData = func(transformedData);
      } catch (error) {
        console.warn(`[Settings] Transformation "${func.name}" threw — skipped:`, error);
      }
    });
    
    cache.set(data, transformedData);
    return transformedData;
  };
})();

/**
 * TEMPORARY: Renames sections to prevent production downtime during section name migration
 * 
 * This function provides fallback section names to ensure the app continues working
 * when backend section names are being changed. This prevents downtime in production.
 * 
 * Fallbacks:
 * - MOST_PLAYED → TOP (if TOP doesn't exist)
 * - TRENDING → TOP_GAMES (if TOP_GAMES doesn't exist)
 * - TOP_GAMES → TRENDING (if TRENDING doesn't exist)
 * 
 * TODO: Remove this function once backend section names are fully migrated and stable
 */
const renameSectionsForBackwardCompatibility = (settingsResponse: SettingsApiResponse): SettingsApiResponse => {
  // Fallback: MOST_PLAYED → TOP
  const hasTopSection = settingsResponse.groups.some(group => group.name === SECTION_NAMES.TOP);
  if (!hasTopSection) {
    const mostPlayedSection = settingsResponse.groups.find(group => group.name === SECTION_NAMES.MOST_PLAYED);
    if (mostPlayedSection) {
      mostPlayedSection.name = SECTION_NAMES.TOP;
    }
  }
  
  // Fallback: TOP_GAMES → TRENDING
  const hasTrendingGamesSection = settingsResponse.groups.some(group => group.name === SECTION_NAMES.TRENDING_GAMES);
  if (!hasTrendingGamesSection) {
    const topGamesSection = settingsResponse.groups.find(group => group.name === SECTION_NAMES.TOP_GAMES);
    if (topGamesSection) {
      topGamesSection.name = SECTION_NAMES.TRENDING_GAMES;
    }
  }
  
  return settingsResponse;
};

const buildNewGameSection = (settingsResponse: SettingsApiResponse): SettingsApiResponse => {
  const newGameSectionRef = settingsResponse.groups.find(group => group.name === 'NEW_GAMES');
  const showFallbackSection = settingsResponse.games.some(({activatedOn, addedToLobbyOn}) => !!activatedOn || !!addedToLobbyOn);

  if (!newGameSectionRef || !showFallbackSection) {
    return settingsResponse;
  }

  // Filter games activated within the last 30 days using the shared helper
  const newGames: ApiGameSettings[] = settingsResponse.games
    .filter(({activatedOn, addedToLobbyOn}) => isGameNew(activatedOn, addedToLobbyOn))
    .sort((a, b) => {
      // Get the most recent date for each game
      const dateA = Math.max(
        new Date(a.activatedOn).getTime(),
        new Date(a.addedToLobbyOn).getTime()
      );
      const dateB = Math.max(
        new Date(b.activatedOn).getTime(),
        new Date(b.addedToLobbyOn).getTime()
      );
      
      // Sort by most recent date (activatedOn or addedToLobbyOn) DESC (newer first)
      return dateB - dateA;
    });

  newGameSectionRef.games = newGames;

  return settingsResponse;
};

// Data transformation functions (similar to old APIIntegrationService)
const addEGTCurrency = (settingsResponse: SettingsApiResponse): SettingsApiResponse => {
  settingsResponse.games.forEach((game) => {
    const denomKeys = Object.keys(game.denominations).sort();
    if (denomKeys.length > 0) {
      const firstKey = denomKeys[0];
      if (firstKey && game.denominations[firstKey]) {
        game.denominations['EGT'] = [...game.denominations[firstKey]];
      }
    }
    if (game.betOptions) {
      const keys = Object.keys(game.betOptions);
      if (keys.length > 0) {
        const firstKey = keys[0];
        if (firstKey && game.betOptions[firstKey]) {
          game.betOptions['EGT'] = [...game.betOptions[firstKey]];
        }
      }
    }
    if (game.bets) {
      const keys = Object.keys(game.bets);
      if (keys.length > 0) {
        const firstKey = keys[0];
        if (firstKey && game.bets[firstKey]) {
          game.bets['EGT'] = game.bets[firstKey];
        }
      }
    }
  });
  return settingsResponse;
};

const mockInstantGamesDenominations = (settingsResponse: SettingsApiResponse): SettingsApiResponse => {
  const getAllAvailableCurrencies = (games: ApiGameSettings[]): string[] => {
    const returnValue: string[] = [];
    games.forEach((game) => {
      const currencies = Object.keys(game.denominations);
      currencies.forEach((currency) => {
        if (!returnValue.includes(currency)) {
          returnValue.push(currency);
        }
      });
    });
    return returnValue;
  };

  const availableCurrencies = getAllAvailableCurrencies(settingsResponse.games);

  settingsResponse.games.forEach((game) => {
    if (game.gameKey.indexOf('Inst') !== -1) {
      const denominations = Object.keys(game.denominations);
      availableCurrencies.forEach((currency) => {
        if (denominations.indexOf(currency) === -1) {
          game.denominations[currency] = [1];
        }
      });
    }
  });

  return settingsResponse;
};

const addSingleProgressivePanel = (data: SettingsApiResponse): SettingsApiResponse => {
  data.groups.forEach((group) => {
    if (group.name.indexOf('PROGRESSIVE') !== -1) {
      group.jackpotName = group.name.toLowerCase();
    }
  });
  return data;
};

const setVerticalBanners = (responseValue: SettingsApiResponse): SettingsApiResponse => {
  responseValue.groups.forEach((group) =>
    group.games.forEach((game) => {
      game.imageLobby = 'vertical';
      game.imageSection = 'vertical';
    })
  );
  return responseValue;
};

// Simple parsing function - can be enhanced later
const parseSettingsResponse = (
  settingsResponse: SettingsApiResponse, 
  _currency: string
): ParsedSettingsResponse => {
  const normalizedGames: Record<string, ApiGameSettings> = normalize('gameKey', settingsResponse.games);

  // First, identify all games that are in the TOP group
  // Note: MOST_PLAYED is already renamed to TOP in the transformation pipeline if TOP doesn't exist
  const topGroupGames = new Set<string>();
  const topGroup = settingsResponse.groups.find(group => group.name === SECTION_NAMES.TOP);
  // The lobby never renders more than MAX_TOP_GAMES Top games, even when the backoffice sends more
  const topGroupGamesList = topGroup
    ? topGroup.games.slice(0, Math.min(topGroup.topLimit || MAX_TOP_GAMES, MAX_TOP_GAMES))
    : [];

  topGroupGamesList.forEach(game => {
    topGroupGames.add(game.gameKey);
  });

  const sections = settingsResponse.groups
    .filter((group) => group.active)
    .filter((group) => group.name === SECTION_NAMES.COLD_GAMES || filterExpAndIntSections(group))
    .sort((a, b) => a.order - b.order)
    .map((group): ISectionSettings | null => {
      try {
        // A single malformed group must not fail the whole load.
        return {
          name: group.name,
          jackpotName: group.jackpotName || null,
          icon: (group.name === SECTION_NAMES.NEW_REL ? SECTION_NAMES.NEW_GAMES : group.name).toLowerCase() + '_icon',
          topLimit: group.topLimit,
          minLimit: group?.minLimit,
          games: (group === topGroup ? topGroupGamesList : group.games).map((gameRef) => {
            const gameData = normalizedGames[gameRef.gameKey];
            // A group may reference a gameKey absent from the games catalog —
            // skip it instead of building a broken IGameSettings.
            if (!gameData) {
              console.warn(`[Settings] Group "${group.name}" references unknown game "${gameRef.gameKey}" — skipped`);
              return null;
            }
            try {
              // A single malformed game must not fail the whole load.
              // Process min/max bets from betRanges or betOptions
              const maxPossibleBets = new Map<string, number>();
              const minPossibleBets = new Map<string, number>();

              if (gameData.betRanges) {
                // Regular games with betRanges
                Object.entries(gameData.betRanges).forEach(([currency, range]) => {
                  if (range && typeof range === 'object' && 'minBet' in range && 'maxBet' in range) {
                    const unitsToDivideBy = 100; // Same as old lobby
                    const typedRange = range as { minBet: number; maxBet: number };
                    minPossibleBets.set(currency, typedRange.minBet / unitsToDivideBy);
                    maxPossibleBets.set(currency, typedRange.maxBet / unitsToDivideBy);
                  }
                });
              } else if (gameData.betOptions) {
                // Multiplayer/instant games with betOptions
                Object.entries(gameData.betOptions).forEach(([currency, options]) => {
                  const unitsToDivideBy = 100; // Same as old lobby
                  const typedOptions = options as number[];
                  const minPlayerBet = typedOptions[0] / unitsToDivideBy;
                  const maxPlayerBet = typedOptions[typedOptions.length - 1] / unitsToDivideBy;
                  minPossibleBets.set(currency, minPlayerBet);
                  maxPossibleBets.set(currency, maxPlayerBet);
                });
              }

              return {
                name: gameData.name,
                gameKey: gameData.gameKey,
                jackpotName: gameData.jackpotName || null,
                displayJackpotName: gameData.displayJackpotName || null,
                order: gameRef.order,
                minDenomination: new Map(Object.entries(gameData.denominations).map(([k, v]) => [k, Math.min(...(v as number[]))])),
                denominations: new Map(Object.entries(gameData.denominations)),
                banners: {
                  lobby: { rows: 2, cols: 1 },
                  section: { rows: 2, cols: 1 },
                },
                maxBet: new Map(), // Legacy field
                maxPossibleBets,
                minPossibleBets,
                isInTournament: gameData.isInTournament || false,
                gameBanners: buildGameBanners(
                  topGroupGames.has(gameRef.gameKey) && BANNER_TAG.TOP,
                  isGameNew(gameData.activatedOn, gameData.addedToLobbyOn) && BANNER_TAG.NEW,
                ),
                statisticRtp: gameData.statisticRtp ?? null,
                gameFeatures: gameData.features,
                maxAwardingCoef: gameData.maxAwardingCoef ?? null,
                paymode: gameData.paymode,
                lines: gameData.lines,
              } as IGameSettings;
            } catch (error) {
              console.warn(`[Settings] Group "${group.name}" game "${gameRef.gameKey}" failed to parse — skipped:`, error);
              return null;
            }
          }).filter((game): game is IGameSettings => game !== null),
          gridBlocks: 0,
          banner: false,
          context: group.context,
        };
      } catch (error) {
        console.warn(`[Settings] Group "${group.name}" failed to parse — skipped:`, error);
        return null;
      }
    })
    .filter((s): s is ISectionSettings => s !== null);

  return {
    sections,
    supportedGames: normalizedGames,
    restrictions: settingsResponse.restrictions,
    supportedLanguages: settingsResponse.languages,
    maxJurisdictionalBet: settingsResponse.maxJurisdictionalBet || [],
    featureFlags: settingsResponse.settings || {},
  };
};

/**
 * Fetches lobby settings from the API with caching optimizations
 */
export const fetchSettings = async (apiHost: string, currency: string): Promise<ParsedSettingsResponse> => {
  // Use cached mock data in development mode
  
  if (isDevelopmentMode(apiHost)) {
    if (cachedMockSettings) {
      return cachedMockSettings;
    }

    // Development mode - using mock settings data
    const data = JSON.parse(JSON.stringify(mockSettingsResponse)); // Deep clone

    // Apply memoized transformations
    const transformedData = memoizedTransformations(data);
    const parsedData = parseSettingsResponse(transformedData, currency);

    // Cache the result for subsequent calls
    cachedMockSettings = parsedData;
    return parsedData;
  }

  // Production mode - make actual API call with caching
  const cacheKey = `${apiHost}_${currency}`;

  // Check if we have a cached transformed response
  const cachedData = transformedResponseCache.get(cacheKey);
  if (cachedData) {
    return parseSettingsResponse(cachedData, currency);
  }

  const url = getSettingsUrl(apiHost, currency.toUpperCase() === 'EGT');

  const response = await fetch(url);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      message: 'Network response was not ok and error response is not valid JSON',
    }));
    throw new Error(errorData.message || `Failed to fetch settings: ${response.statusText} (URL: ${url})`);
  }

  const data = (await response.json()) as SettingsApiResponse;

  // Apply memoized transformations
  const transformedData = memoizedTransformations(data);

  // Cache the transformed response
  transformedResponseCache.set(cacheKey, transformedData);

  return parseSettingsResponse(transformedData, currency);
};